import { FrVrfSpreadsheet } from "@vrf/fr-vrf-spreadsheet-react";
import { stringify } from "../../util/querystring-util";
import React, { useEffect, useMemo } from "react";
import { createPath, useLocation, useNavigate } from "react-router-dom";
import { useAppState } from "../../app";
import { Option } from "../../util/option";
import {
  clearBackenddMessages,
  mappingErrorResponseToSpreadsheet,
} from "../../util/spreadsheet-data-utils";
import {
  DeleteFlagCell,
  Partner,
  PartnerListPageInitialState,
  SpreadSheetState,
  usePageState,
} from "../partner-list";

import {
  useGlobalNotifier,
  useMessage,
  useSpreadsheetState,
} from "@vrf/fr-vrf-core-react";
import {
  FrVrfAccordion,
  FrVrfButton,
  FrVrfButtonPanel,
  FrVrfDropdownField,
  FrVrfFooter,
  FrVrfFormPanel,
  FrVrfPanel,
  FrVrfSingleLineInputField,
} from "@vrf/fr-vrf-web-components-react";
import { ApplicationMessageScreen } from "../../messages";
import {
  useGetDipByRegionList,
  useGetGeneralCodes,
} from "../../services/data-list";
import { useCreateDeployPartnerNames } from "../../services/deployment-request/DeploymentRequestHook";
import { GetPartnerResponse } from "../../services/partner/get-partner/GetPartnerResponse";
import {
  DeleteTargetPartnerInfo,
  GetPartnersRequest,
  UpdatePartnersDeletionFlagsRequest,
  useGetPartners,
  useUpdatePartnersDeletionFlags,
} from "../../services/partners";
import { InDefCommonFlagType } from "../../util/general-code/enums/general-codes";
import { PartnerDetailMode } from "../partner-detail/PartnerDetailPageConstant";
import { usePageNavigation } from "../use-page-navigation";
import {
  REQUEST_CD_TYPES,
  TEMPLATE_FILE_URL,
  createDeploymentRequestParams,
  requestCdTypes,
} from "./PartnerListPageConstant";
import _ from "lodash";
import { Cell } from "@vrf/fr-vrf-spreadsheet-core-templating";

export const PartnerListPageContent = () => {
  const search = useLocation().search;
  const queryParameter = new URLSearchParams(search);
  const initialGetPartnersRequestParameter: GetPartnersRequest = useMemo(() => {
    return {
      cpntId: queryParameter.get("cpntId") ?? "",
      dipByRgn: queryParameter.get("dipByRgn") ?? "",
      ptnrCd: "",
      ptnrName: "",
    };
  }, [queryParameter]);
  const { messageForScreen } = useMessage();
  const navigate = useNavigate();

  const transition = (path: string) => {
    const href = createPath({
      pathname: `${path}`,
    });
    navigate(href);
  };

  const { showToast, showConfirmationDialog, errorMessages, resetUserError } =
    useGlobalNotifier();
  const { isModalPage } = usePageNavigation();

  const [appState, setAppState] = useAppState();
  const [pageState, setPageState] = usePageState();

  const [getGeneralCodesResponse, sendGetGeneralCodesRequest] =
    useGetGeneralCodes();
  const [GetPartnersResponse, sendGetPartnersRequest] = useGetPartners();
  const [updatePartnerListResponse, sendUpdatePartnerListRequest] =
    useUpdatePartnersDeletionFlags();
  const [
    createDeployPartnerNamesResponse,
    sendCreateDeploymentRequestInterfacePartnerNamesRequest,
  ] = useCreateDeployPartnerNames();
  const [getDipByRegionListResponse, sendGetDipByRegionListRequest] =
    useGetDipByRegionList();

  const [spreadsheetState, setSpreadsheetState, initSpreadsheetState] =
    useSpreadsheetState<SpreadSheetState>();

  const { searchResult, dataList, searchField, fieldPath, isInitialized } =
    pageState;

  const getPartnersRequestParameter: GetPartnersRequest = {
    cpntId: searchField.cpntId,
    dipByRgn: searchField.dipByRgn,
    ptnrCd: searchField.ptnrCd ?? "",
    ptnrName: searchField.ptnrName ?? "",
  };

  const getDeleteFlagCells = (partnerList: GetPartnerResponse[]) => {
    let set = new Set();
    let res: DeleteFlagCell = {};
    partnerList.forEach((element: GetPartnerResponse) => {
      if (!set.has(element.ptnrId)) {
        set.add(element.ptnrId);
        res[element.ptnrId] = {
          isDirty: false,
          initialValue: element.delFlg,
          currentValue: "",
        };
      }
    });
    return res;
  };

  const spreadsheetInitialData = useMemo(() => {
    return {
      partnerList: searchResult.partnerList.map((e) => ({
        isEdited: false,
        partnerInfo: e,
      })),
      dataList: dataList,
      deleteFlagCells: getDeleteFlagCells(searchResult.partnerList),
    };
  }, [pageState.searchResult.partnerList, dataList]);

  const onInitialDisplayed = () => {
    resetUserError();
    setAppState({ ...appState, title: "Partner List" });
    setPageState((prevState) => ({
      ...prevState,
      searchField: {
        ...prevState.searchField,
        cpntId: initialGetPartnersRequestParameter.cpntId ?? "",
        dipByRgn: initialGetPartnersRequestParameter.dipByRgn ?? "",
        ptnrCd: "",
        ptnrName: "",
      },
      isInitialized: true,
    }));
    sendGetGeneralCodesRequest({
      cdTypes: requestCdTypes,
    });
    sendGetPartnersRequest(initialGetPartnersRequestParameter);
    sendGetDipByRegionListRequest({});
  };

  const onGetGeneralCodesResponded = () => {
    if (!isInitialized) return;
    if (!getGeneralCodesResponse) return;
    const inDefCommonFlagType =
      getGeneralCodesResponse.gnrlCdTypeList.find(
        (e) => e.cdType === REQUEST_CD_TYPES.IN_DEF_COMMON_FLAG_TYPE,
      )?.cdList ?? [];

    setPageState((prevState) => ({
      ...prevState,
      dataList: {
        ...prevState.dataList,
        inDefCommonFlagType,
      },
    }));
  };

  type MergeItem = {
    parentNum: number;
    childNum: number;
  };
  const createMergeMapFromPartnerList = (partnerList: GetPartnerResponse[]) => {
    const mergeMap: MergeItem[] = partnerList.flatMap((partner, i) => {
      if (partner.chnTypeList.length > 0) {
        const mergeItemList: MergeItem[] = partner.chnTypeList.map((_, j) => ({
          parentNum: i,
          childNum: j,
        }));
        return mergeItemList;
      }
      return { parentNum: i, childNum: i };
    });
    return mergeMap;
  };

  const onGetPartnersRequestResponded = () => {
    if (!isInitialized) return;
    if (!GetPartnersResponse) return;
    const ptnrList = GetPartnersResponse.ptnrList.map((ptnr) => {
      return {
        ...ptnr,
        chnTypeList: ptnr.chnTypeList.filter(
          (chnType) => chnType.chnTypeDelFlg === InDefCommonFlagType.NO,
        ),
      };
    });
    setPageState((prevState) => ({
      ...prevState,
      searchResult: {
        ...prevState.searchResult,
        partnerList: ptnrList,
      },
      mergeMap: createMergeMapFromPartnerList(ptnrList),
    }));
  };

  const onGetDipByRegionListResponded = () => {
    if (!isInitialized) return;
    const dipByRegions = getDipByRegionListResponse?.dipByRgnList ?? [];
    let dropdownDips: Option[] = [];
    dipByRegions.map((e) => {
      let dropdownDip: Option = { label: e.dipByRgn, value: e.dipByRgn };
      dropdownDips.push(dropdownDip);
    });

    setPageState((prevState) => ({
      ...prevState,
      dipByRegionList: getDipByRegionListResponse?.dipByRgnList ?? [],
      dipByRgnDropdown: dropdownDips,
    }));
  };

  const onCreateDeploymentRequestInterfacePartnerNamesResponded = () => {
    if (createDeployPartnerNamesResponse) {
      if (createDeployPartnerNamesResponse.warningMessage) {
        showConfirmationDialog({
          title: "",
          message: createDeployPartnerNamesResponse.warningMessage.join("\n"),
          type: "warning",
          onOkButtonClicked: () => {
            if (!spreadsheetState) return;
            sendCreateDeploymentRequestInterfacePartnerNamesRequest(
              createDeploymentRequestParams(spreadsheetState, true),
            );
          },
        });
        return;
      }

      successToastMessage(ApplicationMessageScreen.APP_SCN_00044);
      sendGetPartnersRequest(getPartnersRequestParameter);
    }
  };

  const onUpdatePartnerListRequestResponded = () => {
    if (!updatePartnerListResponse) {
      return;
    }

    if (Array.isArray(updatePartnerListResponse)) {
      const currentrecords: Partner[] = spreadsheetState
        ? [...spreadsheetState.partnerList]
        : [];

      //clear error
      const currentrecordsWithoutBackendError = currentrecords
        .map((row) => {
          if (row.messageList) {
            return row;
          }
          return { ...row, messageList: [] };
        })
        .map((row) => clearBackenddMessages(row));
      const newSpreadsheetRecordData: Partner[] =
        mappingErrorResponseToSpreadsheet(
          updatePartnerListResponse,
          currentrecordsWithoutBackendError,
          PartnerListPageInitialState.displayColumn,
        );

      if (!spreadsheetState) return;
      setSpreadsheetState({
        ...spreadsheetState,
        partnerList: newSpreadsheetRecordData,
      });
    } else {
      successToastMessage(ApplicationMessageScreen.APP_SCN_00044);
      sendGetPartnersRequest(getPartnersRequestParameter);
    }
  };

  const successToastMessage = (messageId: string) => {
    showToast({
      design: "completion",
      message: messageForScreen(messageId),
    });
  };

  useEffect(onInitialDisplayed, [
    initialGetPartnersRequestParameter.dipByRgn,
    initialGetPartnersRequestParameter.cpntId,
  ]);
  useEffect(onGetGeneralCodesResponded, [
    getGeneralCodesResponse,
    isInitialized,
  ]);
  useEffect(onGetPartnersRequestResponded, [
    GetPartnersResponse,
    isInitialized,
  ]);
  useEffect(onGetDipByRegionListResponded, [
    getDipByRegionListResponse,
    isInitialized,
  ]);
  useEffect(onUpdatePartnerListRequestResponded, [updatePartnerListResponse]);
  useEffect(onCreateDeploymentRequestInterfacePartnerNamesResponded, [
    createDeployPartnerNamesResponse,
  ]);

  const valueChangeHandle = (name: string) => (event: CustomEvent) => {
    if (name === "dipByRgn") {
      setPageState((prevState) => ({
        ...prevState,
        searchField: {
          ...prevState.searchField,
          dipByRgn: event.detail.value,
        },
      }));
    }
    if (name === "cpntId") {
      setPageState((prevState) => ({
        ...prevState,
        searchField: {
          ...prevState.searchField,
          cpntId: event.detail.value,
        },
      }));
    }
    if (name === "ptnrCd") {
      setPageState((prevState) => ({
        ...prevState,
        searchField: {
          ...prevState.searchField,
          ptnrCd: event.detail.value,
        },
      }));
    }
    if (name === "ptnrName") {
      setPageState((prevState) => ({
        ...prevState,
        searchField: {
          ...prevState.searchField,
          ptnrName: event.detail.value,
        },
      }));
    }
  };

  const onCellClicked = (event: CustomEvent) => {
    const eventName = event.detail.name;
    const selectedRow = event.detail.value;

    if (eventName === "ptnrCdClicked" && selectedRow) {
      const navigationParameters = {
        partnerId: selectedRow.partnerInfo.ptnrId,
        mode: PartnerDetailMode.MODIFY,
        godFlg: InDefCommonFlagType.NO,
      };
      if (Boolean(existsEditedRows())) {
        showConfirmationDialog({
          type: "confirm",
          title: "",
          message: messageForScreen(ApplicationMessageScreen.APP_SCN_00033),
          onOkButtonClicked: () => {
            transition(`/partner-detail?${stringify(navigationParameters)}`);
          },
        });
      } else {
        transition(`/partner-detail?${stringify(navigationParameters)}`);
      }
    }
    if (eventName === "ptnrCd_newTabIcon" && selectedRow) {
      const navigationParameters = {
        partnerId: selectedRow.partnerInfo.ptnrId,
        mode: PartnerDetailMode.MODIFY,
        godFlg: InDefCommonFlagType.NO,
      };
      const href = createPath({
        pathname: `/partner-detail?${stringify(navigationParameters)}`,
      });
      window.open(window.location.pathname + "#" + href);
    }
    if (eventName === "cpntIdClicked" && selectedRow) {
      const navigationParameters = {
        cpntId: selectedRow.cpntId,
        dipByRgn: selectedRow.dipByRgn,
        rootCpntId: selectedRow.rootCpntId,
        godFlg: InDefCommonFlagType.NO,
      };
      if (Boolean(existsEditedRows())) {
        showConfirmationDialog({
          type: "confirm",
          title: "",
          message: messageForScreen(ApplicationMessageScreen.APP_SCN_00033),
          onOkButtonClicked: () => {
            transition(`/component-detail?${stringify(navigationParameters)}`);
          },
        });
      } else {
        transition(`/component-detail?${stringify(navigationParameters)}`);
      }
    }
    if (eventName === "cpntId_newTabIcon" && selectedRow) {
      const navigationParameters = {
        cpntId: selectedRow.cpntId,
        dipByRgn: selectedRow.dipByRgn,
        rootCpntId: selectedRow.rootCpntId,
        godFlg: InDefCommonFlagType.NO,
      };
      const href = createPath({
        pathname: `/component-detail?${stringify(navigationParameters)}`,
      });
      window.open(window.location.pathname + "#" + href);
    }
  };

  const onCellEdited = (event: CustomEvent) => {
    if (!spreadsheetState) return;
    const eventName = event.detail.name;
    const selectedRow = event.detail.value;
    const childRowIndex: number = event.detail.rowIndex - 1;
    const rowIndex: number = pageState.mergeMap[childRowIndex].parentNum;
    const value: string = event.detail.cell.value;
    const isChanged: boolean = event.detail.cell.isChanged;

    if (eventName === "delFlgPulldownSelected" && selectedRow) {
      if (
        spreadsheetState?.partnerList[rowIndex].partnerInfo.delFlg === value
      ) {
        return;
      }

      const copyPartnerList: Partner[] = JSON.parse(
        JSON.stringify(spreadsheetState?.partnerList),
      );

      const copyDeleteFlagCells =
        spreadsheetState?.deleteFlagCells as DeleteFlagCell;
      const ptnrId = copyPartnerList[rowIndex].partnerInfo.ptnrId;
      copyDeleteFlagCells[ptnrId].isDirty = event.detail.cell.isDirty;
      copyPartnerList[rowIndex].isEdited = isChanged;
      copyPartnerList[rowIndex].partnerInfo.delFlg = value;

      const newState = {
        ...spreadsheetState,
        partnerList: copyPartnerList,
        deleteFlagCells: copyDeleteFlagCells,
      };
      setSpreadsheetState(newState);
    }
  };

  const onCellBulkEdited = (event: CustomEvent) => {
    if (!spreadsheetState) return;
    const newPartnerList = _.cloneDeep(
      spreadsheetState?.partnerList,
    ) as Partner[];
    const dataNum = newPartnerList.map(
      (item, index) => item.partnerInfo.chnTypeList.length || 1,
    );
    let nextParentIdx = 0;
    const copyDeleteFlagCells =
      spreadsheetState?.deleteFlagCells as DeleteFlagCell;

    event.detail.affectedCells.cells.forEach((item: Cell) => {
      const getIdxs = (rowIdx: number) => {
        let parentIdx = 0;
        let rowSums = 0;
        for (; parentIdx < dataNum.length; parentIdx++) {
          if (rowSums + dataNum[parentIdx] > rowIdx) break;
          rowSums += dataNum[parentIdx];
        }
        return { parentIdx, rowSums };
      };

      const isChanged: boolean = item.isChanged;
      const value: string = item.value;
      const rowIdx: number = item.row.index - 1;
      // there is used union cells in spreadsheet, so need to calculate parent index
      const parentIdx = rowIdx > 0 ? getIdxs(rowIdx).parentIdx : 0;

      if (nextParentIdx > parentIdx) return; // skip if same parent index
      newPartnerList[parentIdx].partnerInfo.delFlg = value;
      newPartnerList[parentIdx].isEdited = isChanged;

      const ptnrId = newPartnerList[parentIdx].partnerInfo.ptnrId;
      copyDeleteFlagCells[ptnrId].isDirty = item.isDirty;

      nextParentIdx++;
    });

    setSpreadsheetState({
      ...spreadsheetState,
      partnerList: newPartnerList,
      deleteFlagCells: copyDeleteFlagCells,
    });
  };

  const validate = (name: string) => {
    if (name === "ptnrNameWtngChng") {
      if (spreadsheetState === undefined) {
        return false;
      }
      const notBlankList = spreadsheetState.partnerList.filter(
        (e) =>
          e.partnerInfo.ptnrNameWtngChng !== "" &&
          e.partnerInfo.ptnrNameWtngChng !== null,
      );
      if (notBlankList.length > 0) {
        return true;
      }
    }
    return false;
  };

  const existsEditedRows = () =>
    spreadsheetState?.partnerList.some((e) => e.isEdited);

  /**
   * Footer
   */
  const clickedSaveHandle = () => {
    resetUserError();
    let deletePartnerInfo: DeleteTargetPartnerInfo[] = [];
    spreadsheetState?.partnerList.forEach((e, index) => {
      if (e.isEdited) {
        deletePartnerInfo.push({
          index: index,
          ptnrId: e.partnerInfo.ptnrId,
          delFlg: e.partnerInfo.delFlg,
          updtDtimePtnr: e.partnerInfo.updtDtimePtnr,
        });
      }
    });

    const updatePartnersDeletionFlagsRequest: UpdatePartnersDeletionFlagsRequest =
      {
        ptnrList: deletePartnerInfo,
      };

    sendUpdatePartnerListRequest(updatePartnersDeletionFlagsRequest);
  };

  const onSaveConfirmDialogHandle = () => {
    showConfirmationDialog({
      title: "",
      message: messageForScreen(ApplicationMessageScreen.APP_SCN_00045),
      type: "confirm",
      onOkButtonClicked: clickedSaveHandle,
    });
  };

  const clickedAddPartnerHandle = () => {
    const navigationParameters = {
      partnerId: "",
      mode: PartnerDetailMode.ADD,
      godFlg: InDefCommonFlagType.NO,
    };

    if (Boolean(existsEditedRows())) {
      showConfirmationDialog({
        type: "confirm",
        title: "",
        message: messageForScreen(ApplicationMessageScreen.APP_SCN_00033),
        onOkButtonClicked: () => {
          transition(`/partner-detail?${stringify(navigationParameters)}`);
        },
      });
    } else {
      transition(`/partner-detail?${stringify(navigationParameters)}`);
    }
  };

  const clickedDeploymentPartnerNameHandle = () => {
    if (!spreadsheetState) return;
    const deployPartnerNames = () => {
      resetUserError();
      sendCreateDeploymentRequestInterfacePartnerNamesRequest(
        createDeploymentRequestParams(spreadsheetState, false),
      );
    };
    if (Boolean(existsEditedRows())) {
      showConfirmationDialog({
        type: "confirm",
        title: "",
        message: messageForScreen(ApplicationMessageScreen.APP_SCN_00033),
        onOkButtonClicked: () => {
          deployPartnerNames();
        },
      });
    } else {
      deployPartnerNames();
    }
  };

  const clickedSearchHandle = () => {
    if (Boolean(existsEditedRows())) {
      showConfirmationDialog({
        type: "confirm",
        title: "",
        message: messageForScreen(ApplicationMessageScreen.APP_SCN_00033),
        onOkButtonClicked: () => {
          resetUserError();
          sendGetPartnersRequest(getPartnersRequestParameter);
        },
      });
    } else {
      resetUserError();
      sendGetPartnersRequest(getPartnersRequestParameter);
    }
  };
  return (
    <>
      <FrVrfAccordion>
        <FrVrfPanel style={{ marginBottom: "10px" }}>
          <FrVrfFormPanel
            style={{
              "--vrf-single-line-input-field-label-width": "150px",
              "--vrf-single-line-input-field-input-width": "200px",
              "--vrf-dropdown-field-label-width": "100px",
              "--vrf-dropdown-field-select-width": "200px",
            }}
            columns={4}
          >
            <FrVrfDropdownField
              labelContent="DIP by Region"
              onValueChanged={valueChangeHandle("dipByRgn")}
              items={pageState.dipByRgnDropdown}
              style={{
                "--vrf-dropdown-field-select-width": "200px",
              }}
              autocomplete={false}
              selectedValue={searchField.dipByRgn}
              errorMessage={errorMessages(fieldPath.dipByRgn, isModalPage())}
            />
            <FrVrfSingleLineInputField
              labelContent="Component ID"
              onInputValueChanged={valueChangeHandle("cpntId")}
              content={searchField.cpntId}
              errorMessage={errorMessages(fieldPath.cpntId, isModalPage())}
            />
            <FrVrfSingleLineInputField
              labelContent="Partner Code"
              onInputValueChanged={valueChangeHandle("ptnrCd")}
              content={searchField.ptnrCd}
              errorMessage={errorMessages(fieldPath.ptnrCd, isModalPage())}
            />
            <FrVrfSingleLineInputField
              labelContent="Partner Name"
              onInputValueChanged={valueChangeHandle("ptnrName")}
              content={searchField.ptnrName}
              errorMessage={errorMessages(fieldPath.ptnrName, isModalPage())}
            />
          </FrVrfFormPanel>
          <FrVrfButtonPanel>
            <FrVrfButton
              content="Search"
              size="medium"
              onClicked={clickedSearchHandle}
            />
          </FrVrfButtonPanel>
        </FrVrfPanel>
      </FrVrfAccordion>
      <div>
        <FrVrfSpreadsheet
          data={spreadsheetInitialData}
          templateUrl={TEMPLATE_FILE_URL}
          onInitialized={initSpreadsheetState}
          onCellClicked={onCellClicked}
          onCellEdited={onCellEdited}
          onCellBulkEdited={onCellBulkEdited}
          maxHeight={650}
        />
      </div>
      {/* Footer */}
      <FrVrfFooter>
        <FrVrfButton
          content="Add Partner"
          size="large"
          onClicked={clickedAddPartnerHandle}
          authActId="partner_list.button.add_pertner"
        />
        <FrVrfButton
          content="Deploy Partner Names"
          size="large"
          onClicked={clickedDeploymentPartnerNameHandle}
          disabled={!Boolean(validate("ptnrNameWtngChng"))}
          authActId="partner_list.button.deploy_partner_names"
        />
        <FrVrfButton
          content="Save"
          size="large"
          disabled={!Boolean(existsEditedRows())}
          onClicked={onSaveConfirmDialogHandle}
          style={{ marginRight: "37px" }}
          authActId="partner_list.button.save"
        />
      </FrVrfFooter>
    </>
  );
};
